import { withAlpha, useAccentFn } from "@/lib/color";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";
import AlboEmbed from "@/components/AlboEmbed";
import LeagueCalendar from "@/components/LeagueCalendar";

// Map division code → albo competition_keys (current + legacy)
const DIV_ALBO_MAP = {
    TOP: ["league_top", "league_serie_a"],
    DOP: ["league_dop", "league_liga"],
    OPS: ["league_ops", "league_premier"],
    HOPE: ["league_hope", "league_ligue1"],
    PRIMAVERA: ["primavera"],
};

// Trophy hero images for each championship (WebP ~150KB)
const DIV_TROPHIES = {
    TOP: "/trophies/campionato-top.webp",
    DOP: "/trophies/campionato-dop.webp",
    OPS: "/trophies/campionato-ops.webp",
    HOPE: "/trophies/campionato-hope.webp",
    PRIMAVERA: "/trophies/campionato-primavera.webp",
};

// --- Qualificazioni coppe europee + promozioni/retrocessioni per posizione (Art. 11 del regolamento) ---
const CUP_STYLES = {
    CL: { short: "CL", label: "Champions League", color: "rgb(var(--tc-2bb6ff))", bg: "rgba(43,182,255,0.13)" },
    EL: { short: "EL", label: "Europa League", color: "rgb(var(--tc-ff6b00))", bg: "rgba(255,107,0,0.13)" },
    CONF: { short: "Conf", label: "Conference League", color: "rgb(var(--tc-7ce38b))", bg: "rgba(124,227,139,0.12)" },
};
const STATUS_STYLES = {
    promo: { short: "Promo", label: "Promozione diretta", color: "rgb(var(--tc-00e676))" },
    playoff: { short: "Play-off", label: "Play-Off promozione", color: "rgb(var(--tc-e8ff00))" },
    playout: { short: "Play-out", label: "Play-Out salvezza", color: "rgb(var(--tc-ff9800))" },
    releg: { short: "Retroc.", label: "Retrocessione diretta", color: "rgb(var(--tc-ff3b30))" },
    safe: { short: "Salva", label: "Salvezza diretta", color: "rgb(var(--tc-14b8a6))" },
};

function cupFor(code, rank) {
    const c = (code || "").toUpperCase();
    if (c === "TOP") return rank <= 6 ? "CL" : "EL";
    if (c === "DOP") return rank <= 4 ? "CL" : rank <= 8 ? "EL" : "CONF";
    if (c === "OPS") return rank <= 2 ? "CL" : rank <= 5 ? "EL" : "CONF";
    if (c === "HOPE") return rank === 1 ? "EL" : "CONF";
    return null;
}

// Mappa rank → stato (promo/playoff/playout/releg/safe) considerando i distacchi a punti.
// Regole condizionali (Art. 11):
//  - Play-off: distacco 3ª-4ª > 9 pt → 3ª PROMOSSA diretta, 4ª/5ª niente play-off.
//  - Play-out: distacco 10ª-11ª > 4 pt → 10ª SALVA; distacco 11ª-12ª > 6 pt → 11ª SALVA, 12ª RETROCESSA.
function computeStatusMap(code, teams) {
    const c = (code || "").toUpperCase();
    const out = {};
    const pts = {};
    for (const t of teams || []) pts[t.rank] = t.points ?? 0;
    const gap = (a, b) => (pts[a] != null && pts[b] != null ? pts[a] - pts[b] : null);

    if (c === "DOP" || c === "OPS" || c === "HOPE") {
        out[1] = "promo";
        out[2] = "promo";
        const g34 = gap(3, 4);
        if (g34 != null && g34 > 9) {
            out[3] = "promo"; // promossa diretta → niente play-off per 4ª/5ª
        } else {
            out[3] = "playoff";
            out[4] = "playoff";
            out[5] = "playoff";
        }
    }
    if (c === "TOP" || c === "DOP" || c === "OPS") {
        out[10] = "playout";
        out[11] = "playout";
        out[12] = "playout";
        out[13] = "releg";
        out[14] = "releg";
        const g1011 = gap(10, 11);
        if (g1011 != null && g1011 > 4) out[10] = "safe";
        const g1112 = gap(11, 12);
        if (g1112 != null && g1112 > 6) {
            out[11] = "safe";
            out[12] = "releg";
        }
    }
    return out;
}

export default function CampionatoDetail() {
    const acc = useAccentFn();
    const { divisionCode } = useParams();
    const [tab, setTab] = useState("standings");
    const [data, setData] = useState(null);
    const [scorers, setScorers] = useState(null);
    const [mvps, setMvps] = useState(null);
    const [gks, setGks] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [s, ts, tm, tg] = await Promise.all([
                    api.get(`/divisions/${divisionCode}/standings`),
                    api.get(`/divisions/${divisionCode}/top-scorers`).catch(() => ({ data: null })),
                    api.get(`/divisions/${divisionCode}/top-mvp`).catch(() => ({ data: null })),
                    api.get(`/divisions/${divisionCode}/top-goalkeepers`).catch(() => ({ data: null })),
                ]);
                setData(s.data);
                setScorers(ts.data);
                setMvps(tm.data);
                setGks(tg.data);
            } catch (e) {
                setErr(e.response?.data?.detail || "Errore");
            } finally {
                setLoading(false);
            }
        })();
    }, [divisionCode]);

    if (loading) {
        return <div className="max-w-7xl mx-auto px-4 py-10 op-label text-t8f95a3 animate-pulse">// CARICAMENTO CLASSIFICA…</div>;
    }
    if (err || !data) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-10">
                <div className="op-card p-6 text-tff4d4d">{err || "Errore"}</div>
            </div>
        );
    }

    const d = data.division;
    const accent = acc(d.accent_color);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="campionato-detail">
            <Link to="/campionati" className="op-label text-t8f95a3 hover:text-tffffff inline-block mb-3">
                ← Tutti i campionati
            </Link>

            <div className="flex items-start gap-4 sm:gap-6">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="op-label" style={{ color: accent }}>
                            // DIVISIONE #{d.order}
                        </div>
                        <span className="op-mono text-xs text-t8f95a3">Stagione {String(data.season || "").split("/")[0]}</span>
                    </div>
                    <h1 className="op-heading text-5xl sm:text-6xl mt-3" style={{ color: accent }}>
                        {d.name}
                    </h1>
                    <p className="text-t8f95a3 text-sm mt-3 max-w-2xl">{d.description}</p>
                </div>
                {DIV_TROPHIES[divisionCode] && (
                    <div
                        className="relative shrink-0 w-24 h-32 sm:w-32 sm:h-44 lg:w-40 lg:h-52 rounded-lg overflow-hidden border border-t22252a"
                        style={{
                            background: `radial-gradient(ellipse at center, ${withAlpha(accent, "33")} 0%, ${withAlpha(accent, "08")} 45%, rgb(var(--tc-0a0b0e)) 100%)`,
                        }}
                        data-testid={`division-trophy-${divisionCode}`}
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: `radial-gradient(circle at center, ${withAlpha(accent, "55")} 0%, transparent 60%)`,
                                filter: "blur(14px)",
                                opacity: 0.5,
                            }}
                        />
                        <img
                            src={DIV_TROPHIES[divisionCode]}
                            alt={`Trofeo ${d.name}`}
                            loading="lazy"
                            className="relative z-10 w-full h-full object-contain p-2"
                            style={{ filter: `drop-shadow(0 0 10px ${withAlpha(accent, "55")})` }}
                        />
                    </div>
                )}
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 mt-8 border-b border-t22252a overflow-x-auto" data-testid="campionato-tabs">
                <SubTab active={tab === "standings"} onClick={() => setTab("standings")} testId="tab-standings" label="Classifica" accent={accent} />
                <SubTab active={tab === "calendar"} onClick={() => setTab("calendar")} testId="tab-calendar" label="Calendario" accent="rgb(var(--tc-2bb6ff))" />
                <SubTab active={tab === "scorers"} onClick={() => setTab("scorers")} testId="tab-scorers" label="Capocannonieri" accent="rgb(var(--tc-ff7a1a))" />
                <SubTab active={tab === "mvp"} onClick={() => setTab("mvp")} testId="tab-mvp" label="MVP" accent="rgb(var(--tc-e8ff00))" />
                <SubTab active={tab === "gk"} onClick={() => setTab("gk")} testId="tab-gk" label="Portieri" accent="rgb(var(--tc-00bcd4))" />
                <SubTab active={tab === "albo"} onClick={() => setTab("albo")} testId="tab-albo" label="Albo d'Oro" accent="rgb(var(--tc-ffd700))" />
            </div>

            {tab === "standings" && <StandingsView data={data} divisionCode={divisionCode} />}
            {tab === "calendar" && <LeagueCalendar divisionCode={divisionCode} accent={accent} />}
            {tab === "scorers" && <PlayersRanking data={scorers} statKey="goals" statLabel="Gol" />}
            {tab === "mvp" && <PlayersRanking data={mvps} statKey="mvp_count" statLabel="MVP" />}
            {tab === "gk" && <PlayersRanking data={gks} statKey="clean_sheets" statLabel="Clean Sheet" secondaryKey="avg_rating" secondaryLabel="Voto medio" />}
            {tab === "albo" && (
                <AlboEmbed
                    competitionKeys={DIV_ALBO_MAP[divisionCode] || []}
                    accent={accent}
                    emptyMessage="Nessun vincitore registrato per questa divisione."
                />
            )}
        </div>
    );
}

function SubTab({ active, onClick, label, testId, accent = "rgb(var(--tc-2bb6ff))" }) {
    return (
        <button
            onClick={onClick}
            className={`op-heading text-base px-4 py-3 border-b-2 transition-colors duration-150 whitespace-nowrap ${
                active ? "text-tffffff" : "text-t8f95a3 hover:text-tffffff"
            }`}
            style={{ borderBottomColor: active ? accent : "transparent" }}
            data-testid={testId}
        >
            {label}
        </button>
    );
}

function StandingsView({ data, divisionCode }) {
    const code = (divisionCode || data.division?.code || "").toUpperCase();
    const statusMap = computeStatusMap(code, data.teams);
    return (
        <>
            {/* Desktop table */}
            <div className="op-card mt-6 overflow-x-auto hidden sm:block" data-testid="standings-table">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b border-t22252a">
                            <th className="py-3 px-3 op-label w-10">#</th>
                            <th className="py-3 px-3 op-label">Squadra</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Partite giocate">PG</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Vittorie">V</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Pareggi">N</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Sconfitte">P</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Gol fatti">GF</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Gol subiti">GS</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Differenza reti">DR</th>
                            <th className="py-3 px-3 op-label text-center w-14" title="Punti">Pt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.teams.map((t) => {
                            const cup = CUP_STYLES[cupFor(code, t.rank)];
                            const status = STATUS_STYLES[statusMap[t.rank]];
                            return (
                            <tr
                                key={t.id}
                                className="border-b border-t1a1d24 transition-colors"
                                style={{
                                    background: cup ? cup.bg : undefined,
                                    borderLeft: `4px solid ${status ? status.color : "transparent"}`,
                                }}
                                data-testid={`row-${t.id}`}
                                title={[cup?.label, status?.label].filter(Boolean).join(" · ")}
                            >
                                <td className="py-2 px-3 op-mono text-t5c6370">{t.rank}</td>
                                <td className="py-2 px-3">
                                    <Link to={`/squadre/${t.id}`} className="flex items-center gap-2 hover:text-t2bb6ff">
                                        {t.logo_url && <img src={t.logo_url} alt="" className="w-6 h-6 object-contain" />}
                                        <span className="text-tffffff">{t.name}</span>
                                        {t.manager_name && <span className="op-mono text-[10px] text-t5c6370">· {t.manager_name}</span>}
                                        {cup && (
                                            <span
                                                className="op-mono text-[9px] uppercase px-1.5 py-0.5 border ml-1"
                                                style={{ color: cup.color, borderColor: withAlpha(cup.color, "66") }}
                                                data-testid={`cup-badge-${t.id}`}
                                            >
                                                {cup.short}
                                            </span>
                                        )}
                                        {status && (
                                            <span
                                                className="op-mono text-[9px] uppercase px-1.5 py-0.5 border"
                                                style={{ color: status.color, borderColor: withAlpha(status.color, "66") }}
                                                data-testid={`status-badge-${t.id}`}
                                            >
                                                {status.short}
                                            </span>
                                        )}
                                    </Link>
                                </td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.played}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.won}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.drawn}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.lost}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.goals_for}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.goals_against}</td>
                                <td className="py-2 px-3 op-mono text-center text-tc9ccd3">{t.goal_diff}</td>
                                <td className="py-2 px-3 op-mono text-center font-bold text-tffffff">{t.points}</td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden mt-6 space-y-2">
                {data.teams.map((t) => {
                    const cup = CUP_STYLES[cupFor(code, t.rank)];
                    const status = STATUS_STYLES[statusMap[t.rank]];
                    return (
                    <Link
                        key={t.id}
                        to={`/squadre/${t.id}`}
                        className="op-card p-3 flex items-center gap-3"
                        style={{
                            background: cup ? cup.bg : undefined,
                            borderLeft: `4px solid ${status ? status.color : "transparent"}`,
                        }}
                        data-testid={`mob-row-${t.id}`}
                    >
                        <div className="op-mono text-sm text-t5c6370 w-6 text-right">{t.rank}</div>
                        {t.logo_url ? (
                            <img src={t.logo_url} alt="" className="w-8 h-8 object-contain shrink-0" />
                        ) : (<div className="w-8 h-8 shrink-0" />)}
                        <div className="flex-1 min-w-0">
                            <div className="text-tffffff text-sm font-semibold truncate">{t.name}</div>
                            {t.manager_name && <div className="op-mono text-[10px] text-t5c6370 truncate">{t.manager_name}</div>}
                            <div className="flex items-center gap-1 mt-1">
                                {cup && (
                                    <span className="op-mono text-[8px] uppercase px-1 py-0.5 border" style={{ color: cup.color, borderColor: withAlpha(cup.color, "66") }}>{cup.short}</span>
                                )}
                                {status && (
                                    <span className="op-mono text-[8px] uppercase px-1 py-0.5 border" style={{ color: status.color, borderColor: withAlpha(status.color, "66") }}>{status.short}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="text-center" data-testid={`mob-played-${t.id}`}>
                                <div className="op-mono text-sm font-semibold text-tc9ccd3">{t.played}</div>
                                <div className="op-mono text-[9px] text-t5c6370">PG</div>
                            </div>
                            <div className="text-right">
                                <div className="op-mono text-lg font-bold text-tffffff">{t.points}</div>
                                <div className="op-mono text-[9px] text-t5c6370">PUNTI</div>
                            </div>
                        </div>
                    </Link>
                    );
                })}
            </div>

            <QualLegend divisionCode={code} teams={data.teams} statusMap={statusMap} />
        </>
    );
}

function QualLegend({ divisionCode, teams, statusMap }) {
    const cups = new Set();
    const statuses = new Set();
    for (const t of teams || []) {
        const c = cupFor(divisionCode, t.rank);
        if (c) cups.add(c);
        const s = statusMap[t.rank];
        if (s) statuses.add(s);
    }
    if (cups.size === 0 && statuses.size === 0) return null;
    return (
        <div className="op-card mt-4 p-4" data-testid="standings-legend">
            <div className="op-label text-t8f95a3 mb-3">// LEGENDA QUALIFICAZIONI</div>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
                {["CL", "EL", "CONF"].filter((k) => cups.has(k)).map((k) => {
                    const s = CUP_STYLES[k];
                    return (
                        <div key={k} className="flex items-center gap-2">
                            <span className="inline-block w-4 h-4 rounded-sm" style={{ background: s.bg, border: `1px solid ${s.color}` }} />
                            <span className="op-mono text-[11px] text-tc9ccd3">{s.label}</span>
                        </div>
                    );
                })}
                {["promo", "playoff", "playout", "releg", "safe"].filter((k) => statuses.has(k)).map((k) => {
                    const s = STATUS_STYLES[k];
                    return (
                        <div key={k} className="flex items-center gap-2">
                            <span className="inline-block w-1.5 h-4 rounded-sm" style={{ background: s.color }} />
                            <span className="op-mono text-[11px] text-tc9ccd3">{s.label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function PlayersRanking({ data, statKey, statLabel, assistKey, secondaryKey, secondaryLabel }) {
    if (!data) {
        return <div className="op-card p-5 mt-6 text-t8f95a3 text-sm">Dati non disponibili.</div>;
    }
    return (
        <div className="mt-6">
            {data.note && (
                <div className="op-card p-3 mb-4 text-xs text-tff7a1a border-tff7a1a/40" style={{ background: "rgba(255,122,26,0.05)" }}>
                    {data.note}
                </div>
            )}
            <div className="op-card overflow-hidden">
                {data.players.length === 0 ? (
                    <div className="p-5 text-t8f95a3 text-sm">Nessun dato.</div>
                ) : (
                    <div className="divide-y divide-t1a1d24">
                        {data.players.map((p) => (
                            <div key={`${p.name}-${p.team_id}`} className="flex items-center gap-3 p-3" data-testid={`rank-row-${p.team_id}-${p.name}`}>
                                <div className="op-mono text-sm text-t5c6370 w-6 text-right">{p.rank}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-tffffff text-sm font-semibold truncate">{p.name}</div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        {p.team_logo && <img src={p.team_logo} alt="" className="w-3.5 h-3.5 object-contain" />}
                                        <Link to={`/squadre/${p.team_id}`} className="op-mono text-[10px] text-t8f95a3 hover:text-t2bb6ff truncate">
                                            {p.team_name}
                                        </Link>
                                        <span className="op-mono text-[10px] text-t5c6370">· {trPositionShort(p.position)}</span>
                                    </div>
                                </div>
                                {assistKey && (
                                    <div className="text-right shrink-0 hidden sm:block">
                                        <div className="op-mono text-sm text-tc9ccd3">{p[assistKey] ?? 0}</div>
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">Assist</div>
                                    </div>
                                )}
                                {secondaryKey && (
                                    <div className="text-right shrink-0 w-14" data-testid={`rank-secondary-${p.team_id}-${p.name}`}>
                                        <div className="op-mono text-sm font-semibold text-t00bcd4">{p[secondaryKey] != null ? p[secondaryKey].toFixed(1) : "—"}</div>
                                        <div className="op-mono text-[9px] text-t5c6370 uppercase">{secondaryLabel}</div>
                                    </div>
                                )}
                                <div className="text-right shrink-0 w-10">
                                    <div className="op-mono text-sm text-t8f95a3">VG {p.vg}</div>
                                </div>
                                <div className="text-right shrink-0 w-14">
                                    <div className="op-mono text-lg font-bold text-tffffff">{p[statKey] ?? 0}</div>
                                    <div className="op-mono text-[9px] text-t5c6370 uppercase">{statLabel}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
